import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/axios';
import UserRowSkeleton from '../../components/Skeleton/UserRowSkeleton';
import Swal from 'sweetalert2'

interface Issue {
    id: number;
    problem_type: string;
    description: string;
    file_path: string | null;
    submission_date: string;
    report_status: 'pending' | 'resolved';
    report_answer: string | null;
    user_id: number;
}

const PendingIssues: React.FC = () => {
    const navigate = useNavigate();

    const [issues, setIssues] = useState<Issue[]>([]);
    const [loading, setLoading] = useState(true);

    const [page, setPage] = useState(1);
    const [limit] = useState(10);
    const [totalPages, setTotalPages] = useState(1);

    useEffect(() => {
        fetchIssues();
    }, [page]);

    const fetchIssues = async () => {
        setLoading(true);
        try {
            const res = await api.get('/getIssue', {
                params: { page, limit, report_status: 'pending' },
            });

            setIssues(res.data.issues || []);
            setTotalPages(res.data.totalPages || 1);
        } catch (error) {
            console.error('Failed to fetch issues', error);
        } finally {
            setLoading(false);
        }
    };

    // id, report_status, report_answer

    // update issue
    const updateIssue = async (id: number) => {
        setLoading(true);
        try {

            Swal.fire({
                title: 'Resolve the issue',
                text: "Please enter a resolution for closing this issue:",
                input: 'textarea', // Textarea input field
                inputPlaceholder: 'Enter resolution here...',
                showCancelButton: true,
                confirmButtonText: 'Submit',
                cancelButtonText: 'Cancel',
                inputValidator: (value) => {
                    if (!value) {
                        return 'You need to provide a resolution!';
                    }
                }
            }).then(async (result) => {
                if (result.isConfirmed) {
                    const res = await api.post('/updateIssue', { id, report_status: 'resolved', report_answer: result.value });
                    if (res.data.status) {
                        Swal.fire('Issue resolved successfully', '', 'success');
                    }
                    fetchIssues();
                }
            })
        } catch (error) {
            console.error('Failed to update issue', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen">
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-800">
                    Pending Issues
                </h1>
                <p className="text-sm text-gray-500">
                    Manage user reported issues
                </p>
            </div>

            <div className="overflow-x-auto rounded-xl border bg-white">
                <table className="w-full text-sm">
                    <thead className="bg-gray-100">
                        <tr>
                            {[
                                'S.No',
                                'Problem Type',
                                'Description',
                                'Attachment',
                                'Submitted On',
                                'Status',
                                'User ID',
                                'Actions',
                            ].map((h) => (
                                <th key={h} className="px-4 py-3 text-left">
                                    {h}
                                </th>
                            ))}
                        </tr>
                    </thead>

                    <tbody>
                        {loading ? (
                            <UserRowSkeleton columns={8} rows={5} />
                        ) : issues.length === 0 ? (
                            <tr>
                                <td
                                    colSpan={8}
                                    className="px-4 py-6 text-center text-gray-500"
                                >
                                    No pending issues found
                                </td>
                            </tr>
                        ) : (
                            issues.map((issue, i) => (
                                <tr key={issue.id} className="border-t">
                                    <td className="px-4 py-2">
                                        {(page - 1) * limit + i + 1}
                                    </td>

                                    <td className="px-4 py-2 font-medium">
                                        {issue.problem_type}
                                    </td>

                                    <td className="px-4 py-2">
                                        {issue.description}
                                    </td>

                                    <td className="px-4 py-2">
                                        {issue.file_path ? (
                                            <a
                                                href={issue.file_path}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-blue-600 underline"
                                            >
                                                View
                                            </a>
                                        ) : (
                                            'N/A'
                                        )}
                                    </td>

                                    <td className="px-4 py-2">
                                        {new Date(
                                            issue.submission_date
                                        ).toLocaleDateString()}
                                    </td>

                                    <td className="px-4 py-2">
                                        <span className={`px-2 py-1 rounded text-xs font-semibold ${issue.report_status === 'resolved' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                            {issue.report_status}
                                        </span>
                                    </td>

                                    <td className="px-4 py-2">
                                        {issue.user_id}
                                    </td>

                                    <td className="px-4 py-2 space-x-2">
                                        <button
                                            onClick={() =>
                                                updateIssue(
                                                    issue.id,
                                                )
                                            }
                                            className="border px-3 py-1 rounded"
                                        >
                                            Resolve
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            <div className="flex justify-end gap-2 mt-4">
                <button
                    disabled={page === 1}
                    onClick={() => setPage((p) => p - 1)}
                    className="px-3 py-1 border rounded disabled:opacity-50"
                >
                    Prev
                </button>

                <span className="px-3 py-1">
                    {page} / {totalPages}
                </span>

                <button
                    disabled={page === totalPages}
                    onClick={() => setPage((p) => p + 1)}
                    className="px-3 py-1 border rounded disabled:opacity-50"
                >
                    Next
                </button>
            </div>
        </div>
    );
};

export default PendingIssues;
